/* eslint-disable @reatom/unit-naming-rule */
import { atom, withInit } from '@reatom/framework'

import { Array, BigNumber as BigNumber_ } from '@blade/std-lib'
import {
  reatomAggregatedPosition,
  type AggregatedPositionModel,
  type WalletPositionModel,
} from '@root/entities/position'
import { TradeSettings } from '@root/entities/trade-settings'
import { walletsAtom } from '@root/entities/wallet'
import { tradeSettingsAtom } from '@root/features/trade/trade-settings'
import { reatomMarketSellControls } from '@root/features/trade/trading-controls/atoms/common'

import type { WalletModel } from '@root/entities/wallet'

export const quickSellModalPositionAtom = atom<WalletPositionModel | undefined>(
  undefined,
  'quickSellModalPositionAtom',
)

export const reatomQuickSellModal = ({
  position,
}: {
  position: AggregatedPositionModel | WalletPositionModel
}) => {
  const pairAtom = atom(position.pair, 'pairAtom')
  const sellTradeSettingsAtom = atom(
    TradeSettings.nullSettings(position.network),
    'sellTradeSettingsAtom',
  ).pipe(
    withInit((ctx) => {
      const tradeSettings = ctx.get(tradeSettingsAtom)
      return tradeSettings
    }),
  )

  const walletAtom = atom<WalletModel | undefined>(
    undefined,
    'walletAtom',
  ).pipe(
    withInit((ctx) => {
      const wallets = ctx.get(walletsAtom)
      if (!reatomAggregatedPosition.is(position)) {
        return wallets.find((it) => it.id === position.walletId)
      }

      // Preselect single wallet if position has only one wallet
      const positionWallets = ctx.get(position.walletPositions)
      const singleWalletPosition = Array.single(positionWallets)
      const singleWallet = singleWalletPosition
        ? wallets.find((it) => it.id === singleWalletPosition.walletId)
        : undefined

      if (singleWallet) {
        return singleWallet
      }

      return wallets
        .flatMap((wallet) => {
          const asset = ctx.get(wallet.assetsByCurrency)[
            position.pair.quote.address
          ]
          if (!asset) {
            return []
          }
          const assetBalance = ctx.get(asset.balance)
          return assetBalance.isZero()
            ? []
            : [{ wallet, balance: assetBalance }]
        })
        .sort((a, b) => BigNumber_.order(a.balance, b.balance))[0]?.wallet
    }),
  )

  const marketModel = reatomMarketSellControls({ pairAtom, walletAtom })

  return {
    ...marketModel,
    tradeSettingsAtom: sellTradeSettingsAtom,
    walletAtom,
  }
}

export type QuickSellModalModel = ReturnType<typeof reatomQuickSellModal>
